import classNames from 'classnames';
import { Trans, useTranslation } from 'next-i18next';

import { Picture } from '../components/picture';
import type { SignupFormProps } from '../components/signup-form';
import { SignupForm } from '../components/signup-form';
import { EYEBROW_CLASS, Stars } from '../components/ui';
import { HERO_FACES, HERO_FORM_ANCHOR, V4A_NAMESPACE } from '../constants';

interface Spark {
  x: string;
  y: string;
  delay: string;
  scale: string;
  side: 'start' | 'end';
}

/* Rune sparks rising along the page gutters (desktop only; hidden under reduced motion). */
const SPARKS: readonly Spark[] = [
  { x: '.18', y: '24%', delay: '0s', scale: '1', side: 'start' },
  { x: '.62', y: '62%', delay: '1.4s', scale: '.7', side: 'start' },
  { x: '.38', y: '12%', delay: '2.6s', scale: '1.15', side: 'start' },
  { x: '.24', y: '20%', delay: '.7s', scale: '.85', side: 'end' },
  { x: '.6', y: '50%', delay: '3.4s', scale: '1', side: 'end' },
  { x: '.4', y: '74%', delay: '2s', scale: '.6', side: 'end' },
];

const SPARK_CLASS = 'spark md:absolute md:top-[var(--sy)] md:h-[6px] md:w-[6px] md:rounded-[50%] md:bg-[color:var(--gold-hi)] md:[box-shadow:0_0_11px_2px_rgb(var(--gold-rgb)_/_.7)]';

function HeroBackground() {
  return (
    <div className='hero__bg pointer-events-none absolute inset-0 z-[-1] overflow-hidden text-[color:var(--gold)]' aria-hidden='true'>
      <span className='hero__night absolute inset-0' />
      <span className='hero__glow hero__glow--violet absolute end-[-16%] top-[-34%] aspect-[1] w-[min(62rem,_96%)] rounded-[50%]' />
      <span className='hero__glow hero__glow--gold absolute start-[-18%] top-[6%] aspect-[1] w-[min(52rem,_90%)] rounded-[50%]' />
      <span className='hero__motes absolute inset-0' />
      <svg className='hero__sigil' viewBox='0 0 520 520' width='520' height='520' focusable='false'>
        <g fill='none' stroke='currentColor' strokeLinecap='round'>
          <circle cx='260' cy='260' r='252' strokeWidth='1' opacity='.5' />
          <circle cx='260' cy='260' r='228' strokeWidth='1' strokeDasharray='2 14' opacity='.7' />
          <circle cx='260' cy='260' r='176' strokeWidth='1' opacity='.35' />
          <path d='M260 84 411 348H109Z' strokeWidth='1' opacity='.45' />
          <path d='M260 436 109 172h302Z' strokeWidth='1' opacity='.28' />
          <g strokeWidth='1.6' opacity='.8'>
            <path d='M260 8v22M260 490v22M8 260h22M490 260h22' />
            <path d='M82 82l16 16M438 82l-16 16M82 438l16-16M438 438l-16-16' />
          </g>
        </g>
      </svg>
      <svg className='hero__sigil hero__sigil--2' viewBox='0 0 620 620' width='620' height='620' focusable='false'>
        <g fill='none' stroke='currentColor' strokeLinecap='round'>
          <circle cx='310' cy='310' r='304' strokeWidth='1' strokeDasharray='1 9' opacity='.85' />
          <circle cx='310' cy='310' r='266' strokeWidth='1' opacity='.4' />
          <g strokeWidth='2' opacity='.9'>
            <path d='M310 6v26M310 588v26M6 310h26M588 310h26' />
            <path d='M96 96l18 18M524 96l-18 18M96 524l18-18M524 524l-18-18' />
          </g>
          <g strokeWidth='1' opacity='.5'>
            <path d='M310 44 546 444H74Z' />
          </g>
        </g>
      </svg>
      <span className='hero__beam absolute start-[58%] top-[-20%] h-[165%] w-[74%] lg:start-1/2 lg:top-[-20%] lg:w-[min(46rem,_60%)]' />
      <span className='hero__vignette absolute inset-0' />
      {SPARKS.map((spark) => (
        <span key={`${spark.side}-${spark.x}-${spark.y}`} className={classNames(SPARK_CLASS, spark.side === 'start' ? 'md:start-[calc(var(--sx)_*_max(0px,_calc(var(--band)_-_16px)))]' : 'spark--r md:end-[calc(var(--sx)_*_max(0px,_calc(var(--band)_-_16px)))] md:start-auto')} style={{ ['--sx' as string]: spark.x, ['--sy' as string]: spark.y, ['--sd' as string]: spark.delay, ['--ss' as string]: spark.scale }} />
      ))}
      <span className='hero__veil absolute inset-0' />
    </div>
  );
}

function HeroSocialProof() {
  const { t } = useTranslation(V4A_NAMESPACE);
  return (
    <div className='hero__social mt-6 flex flex-wrap items-center gap-x-4 gap-y-3 lg:mt-auto lg:pt-6'>
      <span className='faces inline-flex items-center'>
        {HERO_FACES.map((player) => (
          <Picture key={player.slug} name={`player-${player.slug}-face`} widths={[64, 128]} sizes='2rem' alt={player.name} width={128} height={128} className='np-player-avatar -me-2 size-8 rounded-[50%] border border-solid border-[color:rgb(var(--gold-rgb)_/_.45)] bg-[color:#14161B] object-cover object-[50%_30%] [box-shadow:0_2px_8px_rgb(0_0_0_/_.5)]' />
        ))}
        <span className='faces__more me-0 inline-flex h-8 w-auto items-center justify-center whitespace-nowrap rounded-full border border-solid border-transparent bg-[color:var(--accent)] px-[.55rem] py-0 text-[length:11px] font-bold tracking-[.01em] text-[color:var(--accent-ink)] [box-shadow:0_2px_8px_rgb(0_0_0_/_.5)] forced-colors:border forced-colors:border-solid forced-colors:border-[color:CanvasText]'>{t('hero.faces_more')}</span>
      </span>
      <Stars size={15} label={t('rating.label')} className='gap-[.15rem]'>
        <b className='ms-[.4rem] text-[length:var(--fs-sm)] text-[color:var(--fg-strong)]'>{t('rating.short')}</b>
      </Stars>
      <span className='hero__socialtxt text-[length:var(--fs-xs)] text-[color:var(--fg-dim)]'>{t('hero.social')}</span>
    </div>
  );
}

type HeroProps = Omit<SignupFormProps, 'idPrefix'>;

/** "Sua internet está amaldiçoada." Copy and proof on the left, the enrolment card on the right. */
export function Hero(formProps: HeroProps) {
  const { t } = useTranslation(V4A_NAMESPACE);
  return (
    <section className='hero relative isolate pb-[clamp(2.5rem,_1.5rem_+_3vw,_4.5rem)] pt-[clamp(1.75rem,_1rem_+_2.5vw,_3.25rem)] lg:grid lg:min-h-[calc(100svh_-_var(--header-h))] lg:content-center lg:py-[clamp(1.25rem,_3.4vh,_3rem)] lg:[@media(max-height:800px)]:py-2' id='topo' aria-labelledby='hero-title'>
      <HeroBackground />
      <div className="hero__inner mx-auto grid w-full max-w-[var(--container-wide)] gap-[clamp(1.75rem,_1rem_+_2.6vw,_2.75rem)] px-[var(--gutter)] [grid-template-areas:'copy'_'form'] lg:grid-cols-[minmax(0,_1fr)_minmax(0,_1fr)] lg:items-stretch lg:gap-x-[clamp(1.75rem,_.5rem_+_2.4vw,_4rem)] lg:gap-y-[clamp(1.5rem,_1rem_+_1.6vw,_2.25rem)] lg:[grid-template-areas:'copy_form']">
        <div className='hero__copy min-w-0 [grid-area:copy] lg:flex lg:flex-col'>
          <p className={EYEBROW_CLASS}>{t('hero.eyebrow')}</p>
          <h1 className='display hero__title mt-4 text-balance text-[length:clamp(2.25rem,_1.15rem_+_4.1vw,_3.875rem)] font-bold leading-[1.04] tracking-[var(--ls-display)] text-[color:var(--fg-strong)] [overflow-wrap:break-word] lg:text-[length:clamp(2.25rem,_10.6cqi,_3.875rem)]' id='hero-title'>
            <Trans t={t} i18nKey='hero.title' components={{ em: <em className='block not-italic text-[color:var(--accent)] [text-shadow:0_0_34px_rgb(var(--accent-rgb)_/_.38)]' /> }} />
          </h1>
          <p className='hero__lead mt-5 max-w-[32em] text-[length:var(--fs-lg)] leading-[1.62] text-[color:var(--fg-muted)]'>
            <Trans t={t} i18nKey='hero.lead' components={{ b: <b className='font-semibold text-[color:var(--fg-strong)]' /> }} />
          </p>
          <p className='hero__turn mt-4 max-w-[30em] text-[length:var(--fs-lg)] font-semibold leading-normal text-[color:var(--fg)]'>
            <Trans t={t} i18nKey='hero.turn' components={{ em: <em className='not-italic text-[color:var(--accent)]' />, b: <b className='text-[color:var(--fg-strong)]' /> }} />
          </p>
          <HeroSocialProof />
        </div>

        <div className='hero__form mx-auto grid w-full min-w-0 max-w-[var(--form-max)] justify-items-start [grid-area:form] lg:self-start' id={HERO_FORM_ANCHOR} data-cadastro=''>
          <p className='hero__formk m-0 mb-3 inline-flex items-center gap-2 rounded-full border border-solid border-[color:rgb(var(--gold-rgb)_/_.32)] bg-[color:rgb(var(--gold-rgb)_/_.09)] px-[.7rem] py-[.4rem] font-mono text-[length:var(--fs-2xs)] font-medium uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--gold)]'>
            <i className='size-[7px] rounded-none bg-[color:var(--gold)] [box-shadow:0_0_10px_rgb(var(--gold-rgb)_/_.8)]' aria-hidden='true' />
            {t('form.kicker')}
          </p>
          <SignupForm idPrefix='np' {...formProps} />
        </div>
      </div>
    </section>
  );
}
